import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Button from '../../shared/components/ui/Button';
import Table from '../../shared/components/layout/Table';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { transformData } from '../../super-admin/utils/transformData';
import NewPropertyDocumentModal from '../components/forms/NewPropertyDocumentModal';
import Modal from '../../shared/components/ui/Modal';
import NewDocumentTemplate from './NewDocumentTemplate';
import {
  getTemplate,
  getDocumentsByTemplateId,
  downloadDocument,
  uploadDocument,
  downloadSignedDocument,
  downloadSampleDocument,
  deleteTemplate,
  deleteDocument,
  handleCheckTemplateDelete,
  handleCheckDocumentDelete
} from '../services/api/implementation/impl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { act } from 'react';
import EditDocumentTemplate from '../components/forms/EditDocumentTemplate';
import DeleteConfirmationModal from '../components/ui/DeleteConfirmationModal';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import FormInput from '../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import Loader from '../../shared/components/ui/Loader';
import { Plus } from 'lucide-react';
import { useProjectsList } from '../../../contexts/ProjectsContext';

const ProjectDocuments = ({ from }) => {
  const userId = Cookies.get('userId');
  const queryClient = useQueryClient();
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
    watch
  } = useForm();
  const projectID = watch('project');
  const { id } = useParams();
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [isEditTemplateModalOpen, setIsEditTemplateModalOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [canDelete, setCanDelete] = useState(null);
  const [resData, setResData] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteType, setDeleteType] = useState('');
  const [projectId, setProjectId] = useState(null);

  useEffect(() => {
    if (projects) {
      setValue('project', projects?.[0]?.id);
    }
  }, [projects]);

  useEffect(() => {
    if (from === 'sales') {
      setProjectId(projectID);
    } else {
      setProjectId(id);
    }
  }, [from, id, projectID]);

  // Add useEffect to handle navigation state

  // Track page view
  useEffect(() => {
    mixpanel.track('Property Documents Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    // Check if we're returning from template creation
    if (location.state?.fromTemplateCreation) {
      queryClient.invalidateQueries(['template']);
      // Clear the navigation state
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, queryClient, navigate, location.pathname]);

  const {
    data: getAllTemplate,
    isLoading: isLoadingTemplate,
    isError: isErrorTemplate,
    refetch: fetchAllTemplates
  } = useQuery({
    queryKey: ['template', projectId],
    queryFn: () => getTemplate(projectId),
    enabled: !!projectId,
    staleTime: 0, // Consider data as stale immediately
    cacheTime: 0, // Don't cache the data
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true // Refetch when window regains focus
  });

  //write api call for get all documents by template id
  const {
    data: getAllDocumentsByTemplateId,
    isLoading: isLoadingDocuments,
    isError: isErrorDocuments,
    refetch: fetchDocuments
  } = useQuery({
    queryKey: ['documents', selectedTemplateId],
    queryFn: () => getDocumentsByTemplateId(selectedTemplateId),
    enabled: !!selectedTemplateId
  });

  console.log({ getAllDocumentsByTemplateId });

  console.log({ getAllTemplate });

  // -- State to track if the user is viewing the second table --
  const [isViewing, setIsViewing] = useState(false);

  // -- State for showing/hiding the "Create New Document" modal --
  const [showNewDocModal, setShowNewDocModal] = useState(false);

  //write a function to create 2025-02-06T09:45:34.277Z to proper date and time format in Jan 10, 2025 and time in 10:45 AM
  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(date).toLocaleDateString('en-US', options);
  };

  // -- Format + transform for Table 1 --
  const DocumentDataFormat = [
    'id',
    'documentName',
    'description',
    'dateCreated',
    'lastModifiedOn',
    'actions'
  ];

  const RequiredDocumentsData = getAllTemplate?.map((doc) =>
    transformData(
      {
        ...doc,
        documentName: doc?.document_name || '-',
        description: doc?.description || '-',
        dateCreated: formatDate(doc?.created_at) || '-',
        lastModifiedOn: formatDate(doc?.updated_at) || '-'
      },
      DocumentDataFormat
    )
  );

  const tableHeaders = [
    'Document Name',
    'Description',
    'Date Created',
    'Last Modified On',
    'Action'
  ];

  // -- Format + transform for Table 2 --
  const SecondTableDataFormat = ['id', 'customerName', 'unitName', 'dateCreated', 'actions'];

  const secondTableHeaders = ['Customer Name', 'Unit Name', 'Date Created', 'Action'];

  const RequiredSecondTableData = getAllDocumentsByTemplateId?.map((doc) => {
    console.log('Document:', doc);
    return transformData(
      {
        ...doc,
        customerName: doc?.customer_name?.join(',') || '-',
        unitName: doc?.unit?.unitName || '-',
        dateCreated: formatDate(doc?.created_at) || '-',
        actions: `Actions-${doc?.is_uploaded}---${doc?.id}`
      },
      SecondTableDataFormat
    );
  });

  // -- Pagination (dummy) --
  const [page, setPage] = useState(1);
  const limit = 10;
  const totalPages = 1;

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // -- When user clicks View on the first table --
  const handleView = (id) => {
    setSelectedTemplateId(id);
    queryClient.invalidateQueries(['documents', selectedTemplateId]);
    setIsViewing(true);
  };

  // -- Go back to the first table --
  const handleBackToFirstTable = () => {
    setIsViewing(false);
  };

  // -- Create Template => navigate to new template route
  const handleCreateTemplate = () => {
    navigate(`/builder/management/company/projects/project-documents/template/new/${projectId}`, {
      state: { fromTemplateCreation: true }
    });
  };

  // Extract existing deal IDs from the documents
  const existingDealIds = getAllDocumentsByTemplateId?.map((doc) => doc.deal_id) || [];

  // -- Create New Document => open the modal
  const handleCreateNewDocument = () => {
    setShowNewDocModal(true);
  };

  // Define the handler for the new button
  const handleSampleDocument = async (id) => {
    try {
      const data = await downloadSampleDocument(id);
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'SampleDocument.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading sample document:', error);
    }
  };

  const { mutate: downloadDochandler } = useMutation({
    mutationFn: async (id) => {
      return await downloadDocument(id);
    },
    onSuccess: (data, variables) => {
      const id = variables;
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url; // <-- Set the href attribute to the created URL
      a.download = `document_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Document Downloaded');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error in downloading document');
    }
  });

  const { mutate: downloadSignedDocumentHandler } = useMutation({
    mutationFn: async (id) => {
      return await downloadSignedDocument(id);
    },
    onSuccess: (data, variables) => {
      const id = variables;
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url; // <-- Set the href attribute to the created URL
      a.download = `signed_document.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Document Downloaded');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Error in downloading document');
    }
  });

  const handleDownloadDocument = (id) => {
    // console.log('Downloading document with ID:', id);
    // const filteredDoc = getAllDocumentsByTemplateId.find((doc) => parseInt(doc.id) === parseInt(id));
    // const dealId = filteredDoc.deal_id;
    downloadDochandler(id);
  };

  const handleDownloadSignDocument = (id) => {
    // console.log('Downloading document with ID:', id);
    // const filteredDoc = getAllDocumentsByTemplateId.find((doc) => parseInt(doc.id) === parseInt(id));
    // const dealId = filteredDoc.deal_id;
    downloadSignedDocumentHandler(id);
  };

  const handleUploadDocument = async ({ dataNew, payload }) => {
    try {
      await uploadDocument(dataNew, payload);
      toast.success('Document uploaded successfully');
      queryClient.invalidateQueries(['documents', selectedTemplateId]);
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error uploading document');
    }
  };

  const uploadDocumentHandler = (dataNew, payload) => {
    // console.log(dataNew, payload, "uploaded data")
    // const filteredDoc = getAllDocumentsByTemplateId.find((doc) => parseInt(doc.id) === parseInt(dataNew));
    // console.log(filteredDoc, "filtered doc")
    // const dealId = filteredDoc?.deal_id;
    handleUploadDocument({ dataNew, payload });
  };
  const onNewDocSuccess = () => {
    setShowNewDocModal(false);
    queryClient.invalidateQueries(['documents', selectedTemplateId]);
    // setSelectedTemplateId(null);
    // setIsViewing(false);
  };

  const templateEditHandler = (id) => {
    setSelectedTemplateId(id);
    setIsEditTemplateModalOpen(true);
  };

  //delete functions

  const { mutate: handleCheckDelete } = useMutation({
    mutationFn: (id) => {
      if (deleteType === 'template') return handleCheckTemplateDelete(id);
      if (deleteType === 'document') return handleCheckDocumentDelete(id);
      throw new Error('Invalid delete type');
    },

    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async (res, id) => {
      setIsSubmitting(false);
      const charge =
        deleteType === 'template'
          ? getAllTemplate?.find((item) => parseInt(item.id) === parseInt(id))
          : deleteType === 'document'
            ? getAllDocumentsByTemplateId?.find((item) => parseInt(item.id) === parseInt(id))
            : undefined;
      console.log('dfdfdfd', charge);
      const ECName =
        deleteType === 'template' ? charge?.document_name : charge?.customer_name || deleteType;
      if (res.canDelete) {
        setCanDelete('yes');
        setResData({ name: ECName });
        setSelectedIds([parseInt(id)]);
      } else {
        // Fallback if not found
        setResData({ ...res, name: ECName });
        setCanDelete('no');
      }
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error(`Error checking  ${deleteType}`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error checking  ${deleteType}`);
    }
  });

  const { mutate: handleDeleteFn } = useMutation({
    mutationFn: (payload) => {
      if (deleteType === 'template') return deleteTemplate(payload);
      if (deleteType === 'document') return deleteDocument(payload);
      throw new Error('Invalid delete type');
    },
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success(`${deleteType} Deleted Successfully`);
      fetchAllTemplates();
      fetchDocuments();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error(`Error deleting ${deleteType}`, err);
      toast.error(err?.response?.data?.message || err?.message || `Error deleting ${deleteType}`);
    }
  });

  const HandleDelete = (data) => {
    const payload = { ids: data };
    handleDeleteFn(payload);
  };

  return (
    <div className="w-full flex flex-col pl-5 pr-5 bg-gray-100 overflow-hidden">
      <Loader isVisible={isLoadingTemplate || isLoadingDocuments || isProjectsLoading} />
      {/* Modal for creating a new document */}
      {showNewDocModal && (
        <Modal
          name="New Property Document"
          isDirty={true}
          confirmOnClose={true}
          onClose={() => {
            setShowNewDocModal(false);
            setSelectedDeal(null); // Reset the selected deal
          }}
          width={50}>
          <NewPropertyDocumentModal
            onClose={() => {
              setShowNewDocModal(false);
              setSelectedDeal(null); // Reset the selected deal
            }}
            templateId={selectedTemplateId}
            onSuccess={() => {
              onNewDocSuccess();
              setSelectedDeal(null); // Reset the selected deal
            }}
            selectedDeal={selectedDeal}
            onDealSelect={setSelectedDeal}
            existingDealIds={existingDealIds}
            projectId={projectId}
          />
        </Modal>
      )}
      {isEditTemplateModalOpen && (
        <Modal
          name="Edit Document Template"
          isDirty={true}
          confirmOnClose={false}
          onClose={() => setIsEditTemplateModalOpen(false)}
          width={50}>
          <EditDocumentTemplate
            templateid={selectedTemplateId}
            onClose={() => setIsEditTemplateModalOpen(false)}
            onSuccess={() => {
              setIsEditTemplateModalOpen(false);
              queryClient.invalidateQueries(['template']);
            }}
          />
        </Modal>
      )}

      {canDelete === 'no' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={`${deleteType === 'template' ? `Cannot delete '${resData?.name || `this ${deleteType}`}'` : 'Cannot delete this document'} `}
          onClose={() => setCanDelete(null)}
          width={45}>
          {deleteType === 'template' ? (
            <div className="flex flex-col justify-center align-center ">
              <p className="text-center">
                Looks like this template has dependencies, please delete following documents to
                proceed
              </p>
              <div className="flex justify-start, align-middle flex-wrap p-2 rounded-[5px] mt-[4rem]">
                {resData &&
                  resData.reason?.map((r, index) => (
                    <span className="mt-[2px] mr-[4px] text-[#535353]" key={index}>
                      {index + 1}.{r}
                      {index < resData.reason.length - 1 ? ', ' : ''}
                    </span>
                  ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col justify-center align-center ">
              <p className="text-center">Looks like this document is signed.</p>
            </div>
          )}
        </Modal>
      )}

      {canDelete === 'yes' && (
        <Modal
          isDirty={true}
          confirmOnClose={false}
          name={'Confirmation'}
          onClose={() => {
            setCanDelete(null);
            setSelectedIds([]);
          }}
          width={45}>
          <DeleteConfirmationModal
            name={resData.name}
            title={deleteType}
            onClose={() => setCanDelete(null)}
            data={
              deleteType === 'template'
                ? getAllTemplate
                : deleteType === 'document'
                  ? getAllDocumentsByTemplateId
                  : []
            }
            type={deleteType}
            setCanDelete={setCanDelete}
            onDeleteClick={HandleDelete}
            setSelectedIds={setSelectedIds}
            selectedIds={selectedIds}
          />
        </Modal>
      )}

      {/* Header row: back icon (if viewing second table) + top-right button(s) */}
      <div className="w-full flex justify-between items-center py-4">
        {/* Back icon only when second table is visible */}
        {isViewing && (
          <div className="cursor-pointer text-xl font-bold" onClick={handleBackToFirstTable}>
            &lt;
          </div>
        )}
        {!isViewing && from !== 'sales' && <div />}

        {from === 'sales' && !isViewing && (
          <FormInput
            width={30}
            label={'Project'}
            register={register}
            register_key={'project'}
            isRequired={true}
            type={'dropdown'}
            options={projectsForDropdown}
            validation={{ required: 'Project is required' }}
            errors={errors}
          />
        )}

        {/* Top-right button(s) */}
        <div className="flex gap-2">
          {isViewing ? (
            <div className="fixed bottom-8 right-8 z-50">
              <Button
                displayType="primary"
                onClick={handleCreateNewDocument}
                customStyle={{
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}>
                <Plus size={24} color="white" />
              </Button>
            </div>
          ) : (
            <div className="fixed bottom-8 right-8 z-50">
              <Button
                displayType="primary"
                onClick={handleCreateTemplate}
                customStyle={{
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}>
                <Plus size={24} color="white" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* If NOT viewing second table, show first table; else show second table */}
      {!isViewing ? (
        // ---------------- FIRST TABLE ----------------
        <div className="table-container h-[calc(100vh-12rem)] overflow-auto relative ">
          <style>{`
          .table-container .table-body {
            position: static !important;
            overflow-y: visible !important;
            max-height: none !important;
            height: auto !important;
            top: auto !important;
            padding-bottom: 0 !important;
          }
          .table-container .table-container {
            height: auto !important;
          }
          .table-container > div {
            height: auto !important;
          }
        `}</style>
          <Table
            headers={tableHeaders}
            data={RequiredDocumentsData}
            identifier={'id'}
            //isViewEnable={true}
            handleRowClick={handleView}
            isEditEnable={true}
            onEdit={templateEditHandler}
            isDeleteEnable={true}
            onDelete={(id) => {
              handleCheckDelete(id);
              setDeleteType('template');
            }}
            isDownloadSampleDocEnable={true}
            onDownloadSampleDoc={handleSampleDocument}
            isWrap={true}
          />
          {/* <PaginationComponent
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            isFetching={false}
            isEditEnable={false}
          /> */}
        </div>
      ) : (
        // ---------------- SECOND TABLE ----------------
        <div className="h-[25rem] hide-scrollbar overflow-y-scroll relative">
          <Table
            headers={secondTableHeaders}
            data={RequiredSecondTableData}
            identifier={'id'}
            isEditEnable={false}
            isDownloadDocEnable={true}
            onDownloadDoc={handleDownloadDocument}
            onUploadDoc={uploadDocumentHandler}
            onDownloadSignDoc={handleDownloadSignDocument}
            isDeleteEnable={true}
            onDelete={(id) => {
              handleCheckDelete(id);
              setDeleteType('document');
            }}
            isWrap={true}
          />
          {/* <PaginationComponent
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            isFetching={false}
          /> */}
        </div>
      )}
    </div>
  );
};

export default ProjectDocuments;
